"use client";

import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "next/navigation";
import getApiClient from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useBlockableRouter } from "@/components/navigation-block";
import { useUserSummary } from "@/lib/api/user";
import { AuthenticationForm } from "@/app/login/components/authentication-form";
import { ErrorView } from "@/components/error-view";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { components } from "@/lib/api.gen.d";
import { useTranslations } from "next-intl";

type MagicLinkResponse = components["schemas"]["MagicLinkResponse"];

/**
 * Normalize a `LinkAction` value so it can be matched regardless of whether the
 * backend serializes it as PascalCase or snake_case.
 */
function normalizeLinkType(linkType: string): string {
  return linkType.toLowerCase().replace(/_/g, "");
}

export default function LinkPage() {
  const t = useTranslations();
  const params = useParams();
  const router = useBlockableRouter();
  const searchParams = useSearchParams();
  const slug = params.slug as string;
  const confirmed = searchParams.get("confirmed") === "true";

  // Determine login state so we can present a login/signup screen for
  // account-gated links. The summary object is truthy even for guests, so we
  // must check the `user` field specifically.
  const { data: summary, isLoading: userLoading } = useUserSummary();
  const loggedIn = !!summary?.user;

  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authType, setAuthType] = useState<"login" | "signup">("login");
  const [state, setState] = useState<
    | { type: "loading" }
    | { type: "message"; message: string }
    | { type: "confirm"; message: string }
    | { type: "request-access" }
    | { type: "auth" }
    | { type: "error"; kind: "not-found" | "expired" | "generic"; message: string }
  >({ type: "loading" });

  useEffect(() => {
    // Wait until we know whether the visitor is logged in.
    if (userLoading) return;

    const apiClient = getApiClient();

    async function followLink() {
      const { data, error, response } = await apiClient.POST("/api/link/{id}", {
        params: { path: { id: slug } },
        body: { confirmed },
      });

      if (response.status === 404) {
        setState({ type: "error", kind: "not-found", message: t("link.error.not-found") });
        return;
      }
      if (response.status === 410) {
        setState({ type: "error", kind: "expired", message: t("link.error.expired") });
        return;
      }
      if (error || !data) {
        setState({ type: "error", kind: "generic", message: t("link.error.generic") });
        return;
      }

      const result = data as MagicLinkResponse;

      switch (result.action) {
        case "not-found":
          setState({ type: "error", kind: "not-found", message: t("link.error.not-found") });
          break;
        case "expired":
          setState({ type: "error", kind: "expired", message: t("link.error.expired") });
          break;
        case "redirect":
          router.push(result.url);
          router.refresh();
          break;
        case "message":
          setState({ type: "message", message: result.message });
          break;
        case "needs-confirmation": {
          const linkType = normalizeLinkType(String(result.link_type));
          if (linkType === "requesteventaccess") {
            // Requesting access requires an account, then an optional blurb.
            if (loggedIn) {
              setState({ type: "request-access" });
            } else {
              setState({ type: "auth" });
            }
          } else if (linkType === "adminevent" || linkType === "accessorganization") {
            // Becoming an event admin / joining an organization requires an
            // account; once logged in the link re-runs and completes automatically.
            setState({ type: "auth" });
          } else {
            setState({
              type: "confirm",
              message:
                linkType === "confirmemail"
                  ? t("link.confirm.email")
                  : linkType === "loginuser"
                    ? t("link.confirm.login")
                    : t("link.confirm.default"),
            });
          }
          break;
        }
      }
    }

    followLink();
  }, [slug, confirmed, router, t, loggedIn, userLoading]);

  function handleConfirm() {
    router.push(`/link/${slug}?confirmed=true`);
  }

  async function handleSubmitRequest() {
    setSubmitting(true);
    const apiClient = getApiClient();
    const { data, error } = await apiClient.POST("/api/link/{id}", {
      params: { path: { id: slug } },
      body: { confirmed: true, description },
    });
    setSubmitting(false);

    if (error || !data) {
      setState({ type: "error", kind: "generic", message: t("link.error.generic") });
      return;
    }
    const result = data as MagicLinkResponse;
    if (result.action === "message") {
      setState({ type: "message", message: result.message });
    } else if (result.action === "redirect") {
      router.push(result.url);
    } else {
      // Anything else (e.g. the session expired) means the request was not
      // recorded — surface it instead of a false success.
      setState({ type: "error", kind: "generic", message: t("link.error.generic") });
    }
  }

  if (state.type === "error") {
    return (
      <ErrorView
        code={state.kind === "not-found" ? "404" : undefined}
        title={
          state.kind === "not-found"
            ? t("link.error.not-found-title")
            : state.kind === "expired"
              ? t("link.error.expired-title")
              : t("link.error.title")
        }
        description={state.message}
      />
    );
  }

  if (state.type === "auth") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full">
        <p className="max-w-md text-center text-muted-foreground">
          {t("link.auth.prompt")}
        </p>
        <AuthenticationForm
          authType={authType}
          onToggleMethod={() =>
            setAuthType((prev) => (prev === "login" ? "signup" : "login"))
          }
          next={`/link/${slug}`}
          // On success the link is re-followed automatically (the session is
          // now logged in), so just return to the loading state.
          onAuthSuccess={() => setState({ type: "loading" })}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <Card className="w-full max-w-sm">
        {state.type === "loading" && (
          <CardHeader>
            <CardTitle>{t("link.title")}</CardTitle>
            <CardDescription>{t("link.loading.description")}</CardDescription>
          </CardHeader>
        )}

        {state.type === "message" && (
          <>
            <CardHeader>
              <CardTitle>{t("link.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{state.message}</p>
            </CardContent>
          </>
        )}

        {state.type === "confirm" && (
          <>
            <CardHeader>
              <CardTitle>{t("link.confirm.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{state.message}</p>
            </CardContent>
            <CardFooter>
              <Button className="w-full" onClick={handleConfirm}>
                {t("link.confirm.action")}
              </Button>
            </CardFooter>
          </>
        )}

        {state.type === "request-access" && (
          <>
            <CardHeader>
              <CardTitle>{t("link.request-access.title")}</CardTitle>
              <CardDescription>
                {t("link.request-access.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="request-description">
                {t("link.request-access.label")}
              </Label>
              <Textarea
                id="request-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("link.request-access.placeholder")}
              />
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                onClick={handleSubmitRequest}
                disabled={submitting}
              >
                {t("link.request-access.action")}
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}
